import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Hand,
  Smartphone,
  AlertTriangle,
  Loader2,
  ZoomIn,
  MapPin,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";

/**
 * 360° equirectangular video player.
 *
 * - Drag-mode (default): mouse + touch yaw/pitch with pitch clamp at ±85°.
 * - Gyro-mode: DeviceOrientation API. iOS 13+ permission requested on click.
 * - Auto-hide controls after 3s of inactivity while playing.
 * - Tap-to-play overlay until the user starts playback.
 * - WebGL is wrapped in try/catch with a graceful fallback message.
 * - Buffering spinner overlay while the video is waiting on bytes.
 */
export default function Player360({ src, notice = "", hudOverlay = null, poiAnnotations = [], textOverlays = [], showBackButton = false, onBack = null }) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const meshRef = useRef(null);
  const textureRef = useRef(null);
  const rafRef = useRef(0);

  const orientRef = useRef({ yaw: 0, pitch: 0 });
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0 });
  const fovRef = useRef(75);
  const pinchRef = useRef({ active: false, startDist: 0, startFov: 75 });
  const modeRef = useRef("drag");
  const screenOrientRef = useRef(0);
  const hideTimerRef = useRef(0);
  // Phase 10E — current camera direction in radians, updated each RAF, read
  // by the LookHereArrow component via a tick state below.
  const cameraDirRef = useRef({ yaw: 0, pitch: 0 });

  const [mode, setMode] = useState("drag");
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gyroNotice, setGyroNotice] = useState("");
  const [videoError, setVideoError] = useState("");
  const [webglError, setWebglError] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [buffering, setBuffering] = useState(false);
  // Tick that bumps ~10×/s so the POI arrow re-evaluates camera direction.
  const [arrowTick, setArrowTick] = useState(0);

  // ---- Three.js setup ----
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!wrapper || !canvas || !video) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      wrapper.clientWidth / wrapper.clientHeight,
      0.1,
      1100
    );
    camera.position.set(0, 0, 0.01);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    } catch (err) {
      console.warn("WebGL init failed:", err);
      setWebglError(true);
      return undefined;
    }
    if (!renderer.getContext()) {
      setWebglError(true);
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(wrapper.clientWidth, wrapper.clientHeight, false);

    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.format = THREE.RGBAFormat;

    const material = new THREE.MeshBasicMaterial({ map: texture });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    meshRef.current = mesh;
    textureRef.current = texture;

    const zee = new THREE.Vector3(0, 0, 1);
    const euler = new THREE.Euler();
    const q0 = new THREE.Quaternion();
    const q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

    const setQuaternionFromDeviceOrient = (quaternion, alpha, beta, gamma, screenOrient) => {
      euler.set(beta, alpha, -gamma, "YXZ");
      quaternion.setFromEuler(euler);
      quaternion.multiply(q1);
      quaternion.multiply(q0.setFromAxisAngle(zee, -screenOrient));
    };

    const orientationData = { alpha: 0, beta: 0, gamma: 0, has: false };

    const onDeviceOrientation = (e) => {
      if (e.alpha == null) return;
      orientationData.alpha = THREE.MathUtils.degToRad(e.alpha);
      orientationData.beta = THREE.MathUtils.degToRad(e.beta || 0);
      orientationData.gamma = THREE.MathUtils.degToRad(e.gamma || 0);
      orientationData.has = true;
    };
    window.addEventListener("deviceorientation", onDeviceOrientation, true);

    const tick = () => {
      if (modeRef.current === "gyro" && orientationData.has) {
        setQuaternionFromDeviceOrient(
          camera.quaternion,
          orientationData.alpha,
          orientationData.beta,
          orientationData.gamma,
          screenOrientRef.current
        );
        // Extract yaw/pitch from quaternion for POI direction tracking.
        const eOut = new THREE.Euler().setFromQuaternion(camera.quaternion, "YXZ");
        cameraDirRef.current.yaw = eOut.y;
        cameraDirRef.current.pitch = eOut.x;
      } else {
        const { yaw, pitch } = orientRef.current;
        const e = new THREE.Euler(pitch, yaw, 0, "YXZ");
        camera.quaternion.setFromEuler(e);
        cameraDirRef.current.yaw = yaw;
        cameraDirRef.current.pitch = pitch;
      }
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const ro = new ResizeObserver(() => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    });
    ro.observe(wrapper);

    const onOrient = () => {
      screenOrientRef.current = THREE.MathUtils.degToRad(window.orientation || 0);
    };
    onOrient();
    window.addEventListener("orientationchange", onOrient);

    // ---- Zoom: mouse wheel (desktop) + 2-finger pinch (mobile) ----
    const MIN_FOV = 30;
    const MAX_FOV = 100;
    const applyFov = (target) => {
      const next = Math.max(MIN_FOV, Math.min(MAX_FOV, target));
      if (next === camera.fov) return;
      camera.fov = next;
      fovRef.current = next;
      camera.updateProjectionMatrix();
    };
    const onWheel = (ev) => {
      ev.preventDefault();
      applyFov(camera.fov + ev.deltaY * 0.05);
    };
    const pinchDist = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );
    const onTouchStart = (ev) => {
      if (ev.touches.length === 2) {
        pinchRef.current = {
          active: true,
          startDist: pinchDist(ev.touches),
          startFov: camera.fov,
        };
        dragRef.current.active = false;
        ev.preventDefault();
      }
    };
    const onTouchMove = (ev) => {
      if (!pinchRef.current.active || ev.touches.length !== 2) return;
      const dist = pinchDist(ev.touches);
      if (!dist) return;
      const ratio = dist / pinchRef.current.startDist;
      if (!ratio || !Number.isFinite(ratio)) return;
      applyFov(pinchRef.current.startFov / ratio);
      ev.preventDefault();
    };
    const onTouchEnd = (ev) => {
      if (ev.touches.length < 2) pinchRef.current.active = false;
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd);
    canvas.addEventListener("touchcancel", onTouchEnd);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener("orientationchange", onOrient);
      window.removeEventListener("deviceorientation", onDeviceOrientation, true);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("touchcancel", onTouchEnd);
      geometry.dispose();
      material.dispose();
      texture.dispose();
      renderer.dispose();
    };
  }, []);

  const onPointerDown = (e) => {
    if (modeRef.current !== "drag") return;
    if (pinchRef.current.active) return;
    dragRef.current.active = true;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    bumpControls();
    if (pinchRef.current.active) return;
    if (!dragRef.current.active || modeRef.current !== "drag") return;
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    const w = wrapperRef.current?.clientWidth || 1;
    const h = wrapperRef.current?.clientHeight || 1;
    orientRef.current.yaw -= (dx / w) * Math.PI;
    orientRef.current.pitch -= (dy / h) * Math.PI;
    const maxPitch = THREE.MathUtils.degToRad(85);
    orientRef.current.pitch = Math.max(-maxPitch, Math.min(maxPitch, orientRef.current.pitch));
  };
  const onPointerUp = (e) => {
    dragRef.current.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // ---- Video handlers ----
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onTime = () => setCurrentTime(v.currentTime);
    const onMeta = () => {
      setDuration(v.duration || 0);
      setVideoError("");
    };
    const onPlay = () => {
      setIsPlaying(true);
      setHasStarted(true);
      setVideoError("");
    };
    const onPause = () => setIsPlaying(false);
    const onErr = () => {
      const code = v.error?.code;
      if (code === 3 || code === 4) {
        setVideoError("Video konnte nicht geladen werden.");
      }
    };
    const onLoadedData = () => {
      setVideoError("");
      setBuffering(false);
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onCanPlay = () => setBuffering(false);
    const onStalled = () => setBuffering(true);

    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("error", onErr);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("stalled", onStalled);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("error", onErr);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("stalled", onStalled);
    };
  }, []);

  // Reset start state when the underlying src changes (e.g. switch from
  // original → mobile after processing completes mid-session).
  useEffect(() => {
    setHasStarted(false);
    setBuffering(false);
  }, [src]);

  // Phase 10E — tick every 100ms while playing so the look-here arrow keeps
  // re-evaluating camera direction. Skipped while paused / not started.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = setInterval(() => setArrowTick((t) => (t + 1) % 1000), 100);
    return () => clearInterval(id);
  }, [isPlaying]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => setVideoError("Wiedergabe blockiert. Erneut tippen."));
    } else {
      v.pause();
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const onSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.clientX != null ? e.clientX : e.touches?.[0]?.clientX;
    if (clientX == null) return;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    v.currentTime = pct * duration;
  };

  const enterGyroMode = useCallback(async () => {
    setGyroNotice("");
    const DOE = typeof window !== "undefined" ? window.DeviceOrientationEvent : null;
    if (!DOE) {
      setGyroNotice(
        "Handybewegung wird auf diesem Gerät oder Browser nicht unterstützt. Bitte per Finger wischen."
      );
      setMode("drag");
      modeRef.current = "drag";
      return;
    }
    if (typeof DOE.requestPermission === "function") {
      try {
        const result = await DOE.requestPermission();
        if (result !== "granted") {
          setGyroNotice("Handybewegung wurde nicht erlaubt. Bitte per Finger wischen.");
          setMode("drag");
          modeRef.current = "drag";
          return;
        }
      } catch {
        setGyroNotice(
          "Handybewegung wird auf diesem Gerät oder Browser nicht unterstützt. Bitte per Finger wischen."
        );
        setMode("drag");
        modeRef.current = "drag";
        return;
      }
    }
    setMode("gyro");
    modeRef.current = "gyro";
    let received = false;
    const probe = (e) => {
      const hasData =
        (typeof e.alpha === "number" && e.alpha !== 0) ||
        (typeof e.beta === "number" && e.beta !== 0) ||
        (typeof e.gamma === "number" && e.gamma !== 0) ||
        e.absolute === true;
      if (hasData) {
        received = true;
        window.removeEventListener("deviceorientation", probe);
      }
    };
    window.addEventListener("deviceorientation", probe);
    setTimeout(() => {
      window.removeEventListener("deviceorientation", probe);
      if (!received) {
        setGyroNotice(
          "Handybewegung wird auf diesem Gerät oder Browser nicht unterstützt. Bitte per Finger wischen."
        );
        setMode("drag");
        modeRef.current = "drag";
      }
    }, 1500);
  }, []);

  const enterDragMode = () => {
    setMode("drag");
    modeRef.current = "drag";
    setGyroNotice("");
  };

  const bumpControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!videoRef.current?.paused) setControlsVisible(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      setControlsVisible(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = 0;
      }
    } else {
      bumpControls();
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [isPlaying, bumpControls]);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    if (!document.fullscreenElement) {
      wrap.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const fmt = (s) => {
    if (!s || !Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (webglError) {
    return (
      <div
        ref={wrapperRef}
        data-testid="player360-wrapper"
        className="relative w-full bg-black select-none rounded-xl overflow-hidden aspect-video flex items-center justify-center"
        style={{ minHeight: 320 }}
      >
        <div
          className="text-center px-6 py-8 max-w-md"
          data-testid="player360-webgl-fallback"
        >
          <div className="inline-flex w-12 h-12 items-center justify-center rounded-2xl bg-[#1f1f23] mb-4">
            <AlertTriangle size={22} className="text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">
            Dein Browser unterstützt WebGL nicht
          </h3>
          <p className="text-sm text-[#9a9aa3] mt-2 leading-relaxed">
            Für die 360°-Darstellung benötigen wir WebGL. Bitte aktualisiere
            deinen Browser, aktiviere Hardware-Beschleunigung oder probiere ein
            anderes Gerät.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      data-testid="player360-wrapper"
      className="relative w-full bg-black select-none rounded-xl overflow-hidden aspect-video group"
      style={{ minHeight: 320 }}
      onMouseMove={bumpControls}
      onTouchStart={bumpControls}
    >
      <video
        ref={videoRef}
        src={src}
        crossOrigin="anonymous"
        playsInline
        muted={muted}
        preload="metadata"
        className="hidden"
        data-testid="player360-video"
      />

      <canvas
        ref={canvasRef}
        data-testid="player360-canvas"
        className="absolute inset-0 w-full h-full touch-none cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      {!hasStarted && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-[2px] hover:bg-black/40 transition-colors"
          aria-label="Video abspielen"
          data-testid="player360-tap-to-play"
        >
          <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 border border-white/20 backdrop-blur-md group-hover:scale-105 transition-transform">
            <Play size={32} className="text-white translate-x-0.5" fill="currentColor" />
          </span>
        </button>
      )}

      {/* Buffering spinner */}
      {hasStarted && buffering && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
          data-testid="player360-buffering"
        >
          <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black/55 backdrop-blur">
            <Loader2 size={28} className="text-white animate-spin" />
          </span>
        </div>
      )}

      {videoError && (
        <div
          className="absolute top-3 left-3 right-3 mx-auto max-w-md bg-black/80 text-red-300 text-xs px-3 py-2 rounded-md pointer-events-none z-30"
          data-testid="player360-error"
        >
          {videoError}
        </div>
      )}

      {gyroNotice && (
        <div
          className="absolute top-3 left-3 right-3 mx-auto max-w-md bg-black/80 text-amber-300 text-xs px-3 py-2 rounded-md z-30"
          data-testid="player360-gyro-notice"
        >
          {gyroNotice}
        </div>
      )}

      {/* Optional caller-supplied notice (e.g. "wird für Mobile optimiert") */}
      {notice && (
        <div
          className="absolute top-3 left-3 right-3 mx-auto max-w-md bg-black/80 text-sky-200 text-xs px-3 py-2 rounded-md z-30 flex items-center gap-2"
          data-testid="player360-notice"
        >
          <Loader2 size={12} className="animate-spin shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {/* Mode segmented switch */}
      <div
        className={`absolute top-3 right-3 z-10 transition-opacity duration-300 ${
          controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid="player360-mode-segmented"
      >
        <div className="inline-flex items-center p-0.5 rounded-full bg-black/70 border border-white/15 backdrop-blur">
          <SegmentButton
            active={mode === "drag"}
            onClick={enterDragMode}
            icon={<Hand size={14} />}
            label="Wischen"
            testId="player360-mode-drag"
          />
          <SegmentButton
            active={mode === "gyro"}
            onClick={enterGyroMode}
            icon={<Smartphone size={14} />}
            label="Handy bewegen"
            testId="player360-mode-gyro"
          />
        </div>
        <div
          className="mt-2 flex justify-end"
          data-testid="player360-zoom-hint"
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 border border-white/10 backdrop-blur text-[11px] text-white/85">
            <ZoomIn size={12} className="shrink-0" />
            <span className="hidden md:inline">Mausrad zum Zoomen</span>
            <span className="md:hidden">2 Finger spreizen zum Zoomen</span>
          </div>
        </div>
      </div>

      {/* Phase 10F — Back button for logged-in admins. Sits above any HUD/POI. */}
      {showBackButton && onBack && (
        <button
          onClick={onBack}
          className="absolute top-3 left-3 sm:top-4 sm:left-4 z-30 inline-flex items-center gap-1.5 h-10 px-3 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white text-xs sm:text-sm font-medium hover:bg-black/80 transition-colors"
          data-testid="player360-back-button"
          aria-label="Zurück zur Bibliothek"
        >
          <ArrowLeft size={16} />
          <span className="hidden sm:inline">Zur Bibliothek</span>
        </button>
      )}

      {/* Phase 10D — HUD Overlay (Standort + Datum) */}
      <HUDOverlayBadge
        overlay={hudOverlay}
        currentTime={currentTime}
        visible={hasStarted}
      />

      {/* Phase 10G — Free-form text overlays */}
      <TextOverlayLayer
        overlays={textOverlays}
        currentTime={currentTime}
        visible={hasStarted}
      />

      {/* Phase 10E — Look-here arrow */}
      <LookHereArrow
        annotations={poiAnnotations}
        currentTime={currentTime}
        cameraDirRef={cameraDirRef}
        wrapperRef={wrapperRef}
        tick={arrowTick}
        visible={hasStarted}
        modeRef={modeRef}
        orientRef={orientRef}
      />

      {/* Bottom controls */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-3 pb-3 pt-10 z-10 transition-opacity duration-300 ${
          controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="h-3 bg-white/15 rounded-full cursor-pointer mb-3 relative group/bar"
          onClick={onSeek}
          onTouchStart={onSeek}
          data-testid="player360-progress-bar"
        >
          <div
            className="h-3 rounded-full bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] transition-[width] duration-100"
            style={{ width: `${pct}%` }}
            data-testid="player360-progress-fill"
          />
        </div>

        <div className="flex items-center justify-between gap-2 text-neutral-100">
          <div className="flex items-center gap-2">
            <IconButton onClick={togglePlay} testId="player360-play-button" label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? <Pause size={20} /> : <Play size={20} fill="currentColor" />}
            </IconButton>
            <IconButton onClick={toggleMute} testId="player360-mute-button" label={muted ? "Ton an" : "Stumm"}>
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </IconButton>
            <span className="text-xs text-neutral-300 tabular-nums" data-testid="player360-time-display">
              {fmt(currentTime)} / {fmt(duration)}
            </span>
          </div>
          <IconButton
            onClick={toggleFullscreen}
            testId="player360-fullscreen-button"
            label={isFullscreen ? "Vollbild beenden" : "Vollbild"}
          >
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </IconButton>
        </div>
      </div>
    </div>
  );
}

function IconButton({ children, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-colors text-white"
    >
      {children}
    </button>
  );
}

function SegmentButton({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={
        "inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-full text-xs font-medium transition-all " +
        (active
          ? "bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white shadow-[0_4px_14px_rgba(255,75,80,0.4)]"
          : "text-white/80 hover:text-white")
      }
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ============================================================
// Phase 10D — HUD Overlay Badge (Location + Date)
// Pure DOM overlay above the WebGL canvas. Visible only between
// show_at_sec and show_at_sec + duration_sec. Fades in/out smoothly.
// ============================================================
function HUDOverlayBadge({ overlay, currentTime, visible }) {
  if (!overlay || !visible) return null;
  const start = Number(overlay.show_at_sec || 0);
  const dur = Number(overlay.duration_sec || 4);
  const end = start + dur;
  const t = Number(currentTime || 0);
  // Smooth fade: 300ms ease-in at the start, 500ms ease-out at the end.
  let opacity = 0;
  if (t >= start && t < end) {
    const fadeIn = Math.min(1, (t - start) / 0.3);
    const fadeOut = Math.min(1, (end - t) / 0.5);
    opacity = Math.max(0, Math.min(1, Math.min(fadeIn, fadeOut)));
  }
  if (opacity <= 0) return null;

  const posClass = {
    "bottom-left": "bottom-20 left-4 sm:bottom-24 sm:left-6",
    "bottom-right": "bottom-20 right-4 sm:bottom-24 sm:right-6",
    "top-left": "top-4 left-4 sm:top-6 sm:left-6",
    "top-right": "top-4 right-4 sm:top-6 sm:right-6",
  }[overlay.position] || "bottom-20 left-4 sm:bottom-24 sm:left-6";

  // Phase 10F — relative font size on the DOM badge.
  const sizeKey = overlay.font_size || "medium";
  const textSize =
    sizeKey === "small" ? "text-[11px] sm:text-xs"
    : sizeKey === "large" ? "text-base sm:text-lg"
    : "text-xs sm:text-sm";
  const iconSize = sizeKey === "small" ? 12 : sizeKey === "large" ? 18 : 14;

  return (
    <div
      className={`absolute ${posClass} z-20 pointer-events-none transition-opacity`}
      style={{ opacity, transitionDuration: "200ms" }}
      data-testid="player360-hud-overlay"
    >
      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black/60 backdrop-blur-md border border-white/10 text-white ${textSize} shadow-lg`}>
        {overlay.location && (
          <span className="inline-flex items-center gap-1.5" data-testid="player360-hud-location">
            <MapPin size={iconSize} className="shrink-0 text-white/80" />
            <span className="font-medium">{overlay.location}</span>
          </span>
        )}
        {overlay.location && overlay.date && (
          <span className="text-white/30">·</span>
        )}
        {overlay.date && (
          <span className="text-white/85" data-testid="player360-hud-date">{overlay.date}</span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Phase 10G — Free-form text overlay layer
// Multiple text overlays per video, plain or with-background style.
// Each entry handles its own time-based visibility + fade.
// ============================================================
const TEXT_POS_CLASS = {
  "top-left": "top-4 left-4 sm:top-6 sm:left-6 text-left",
  "top-center": "top-4 left-1/2 -translate-x-1/2 sm:top-6 text-center",
  "top-right": "top-4 right-4 sm:top-6 sm:right-6 text-right",
  "bottom-left": "bottom-20 left-4 sm:bottom-24 sm:left-6 text-left",
  "bottom-center": "bottom-20 left-1/2 -translate-x-1/2 sm:bottom-24 text-center",
  "bottom-right": "bottom-20 right-4 sm:bottom-24 sm:right-6 text-right",
};

function TextOverlayLayer({ overlays, currentTime, visible }) {
  if (!visible || !overlays || overlays.length === 0) return null;
  return (
    <div className="absolute inset-0 z-20 pointer-events-none" data-testid="player360-text-overlays">
      {overlays.map((o) => (
        <SingleTextOverlay key={o.id} overlay={o} currentTime={currentTime} />
      ))}
    </div>
  );
}

function SingleTextOverlay({ overlay, currentTime }) {
  const start = Number(overlay.start_sec || 0);
  const end = start + Number(overlay.duration_sec || 4);
  const t = Number(currentTime || 0);
  let opacity = 0;
  if (t >= start && t < end) {
    const fadeIn = Math.min(1, (t - start) / 0.3);
    const fadeOut = Math.min(1, (end - t) / 0.5);
    opacity = Math.max(0, Math.min(1, Math.min(fadeIn, fadeOut)));
  }
  if (opacity <= 0) return null;

  const posClass = TEXT_POS_CLASS[overlay.position] || TEXT_POS_CLASS["bottom-center"];
  const sizeKey = overlay.font_size || "medium";
  const textSize =
    sizeKey === "small" ? "text-sm sm:text-base"
    : sizeKey === "large" ? "text-2xl sm:text-3xl"
    : "text-base sm:text-xl";

  const isPlain = overlay.style !== "with_background";
  const color = overlay.text_color || "#FFFFFF";

  // Plain style: text-shadow + subtle stroke. With-background: semi dark box.
  const baseStyle = isPlain
    ? {
        color,
        textShadow: "0 2px 4px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.6)",
        WebkitTextStroke: "0.5px rgba(0,0,0,0.35)",
      }
    : { color };

  return (
    <div
      className={`absolute ${posClass} transition-opacity max-w-[80%] sm:max-w-[60%]`}
      style={{ opacity, transitionDuration: "200ms" }}
      data-testid={`player360-text-overlay-${overlay.id}`}
    >
      {isPlain ? (
        <div
          className={`${textSize} font-semibold leading-snug whitespace-pre-wrap drop-shadow-md`}
          style={baseStyle}
        >
          {overlay.text}
        </div>
      ) : (
        <div
          className={`${textSize} inline-block font-medium leading-snug whitespace-pre-wrap px-3 py-2 rounded-lg bg-black/60 backdrop-blur-md border border-white/10 shadow-lg`}
          style={baseStyle}
        >
          {overlay.text}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Phase 10E — Look-Here Arrow
// Iterates active POI annotations, computes angular delta between camera
// and target. If delta > 30°, shows a pulsating arrow at the viewport edge
// pointing towards the target.
// ============================================================
const NORM_PI = (a) => {
  // Wrap any angle into (-PI, PI].
  let x = a;
  while (x > Math.PI) x -= 2 * Math.PI;
  while (x <= -Math.PI) x += 2 * Math.PI;
  return x;
};

function activePOI(annotations, currentTime) {
  const t = Number(currentTime || 0);
  for (const p of annotations || []) {
    const s = Number(p.start_sec || 0);
    const d = Number(p.duration_sec || 4);
    if (t >= s && t < s + d) return p;
  }
  return null;
}

function LookHereArrow({ annotations, currentTime, cameraDirRef, wrapperRef, tick, visible, modeRef, orientRef }) {
  // `tick` is unused but is what triggers the re-render every 100ms while
  // playing. Without it React would not re-evaluate camera direction.
  void tick;
  // Phase 10F — first-time tooltip flag (persisted in localStorage so the
  // hint only shows once per device).
  const [showTooltip, setShowTooltip] = useState(false);
  const [hovered, setHovered] = useState(false);
  const lastSeenPoiIdRef = useRef(null);
  const tooltipTimerRef = useRef(0);
  const tweenRafRef = useRef(0);

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      if (tweenRafRef.current) cancelAnimationFrame(tweenRafRef.current);
    };
  }, []);

  if (!visible) return null;
  const poi = activePOI(annotations, currentTime);
  if (!poi) return null;

  const cam = cameraDirRef.current || { yaw: 0, pitch: 0 };
  const tgtYaw = (poi.target_yaw_deg || 0) * Math.PI / 180;
  const tgtPitch = (poi.target_pitch_deg || 0) * Math.PI / 180;

  const dYaw = NORM_PI(tgtYaw - cam.yaw);
  const dPitch = NORM_PI(tgtPitch - cam.pitch);

  const totalDeg = Math.sqrt(dYaw * dYaw + dPitch * dPitch) * 180 / Math.PI;
  if (totalDeg <= 30) return null;

  // Phase 10F — fire first-time tooltip once per device on the first POI we
  // actually surface (any video, any POI).
  if (lastSeenPoiIdRef.current !== poi.id) {
    lastSeenPoiIdRef.current = poi.id;
    try {
      if (typeof window !== "undefined" &&
          !window.localStorage.getItem("poi_tooltip_seen")) {
        window.localStorage.setItem("poi_tooltip_seen", "1");
        setShowTooltip(true);
        if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = setTimeout(() => setShowTooltip(false), 4000);
      }
    } catch (_e) { /* private-mode / storage disabled */ }
  }

  const screenAngle = Math.atan2(-dPitch, dYaw);
  const cosA = Math.cos(screenAngle);
  const sinA = Math.sin(screenAngle);

  const w = wrapperRef.current?.clientWidth || 800;
  const h = wrapperRef.current?.clientHeight || 450;
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(w, h) * 0.4;
  const x = cx + cosA * radius;
  const y = cy + sinA * radius;
  const rotDeg = (screenAngle * 180 / Math.PI);

  // Click-to-tween only in drag-mode (gyro mode is steered by the device).
  const inGyro = modeRef?.current === "gyro";
  const handleClick = (ev) => {
    ev?.stopPropagation?.();
    if (inGyro || !orientRef?.current) return;
    if (tweenRafRef.current) cancelAnimationFrame(tweenRafRef.current);
    const startYaw = orientRef.current.yaw;
    const startPitch = orientRef.current.pitch;
    // Use shortest-path delta for both yaw and pitch.
    const endYaw = startYaw + NORM_PI(tgtYaw - startYaw);
    const endPitch = startPitch + NORM_PI(tgtPitch - startPitch);
    const dur = 1000;
    const t0 = performance.now();
    const easeInOutCubic = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const k = easeInOutCubic(p);
      orientRef.current.yaw = startYaw + (endYaw - startYaw) * k;
      orientRef.current.pitch = Math.max(-Math.PI / 2 * 0.94,
        Math.min(Math.PI / 2 * 0.94, startPitch + (endPitch - startPitch) * k));
      if (p < 1) tweenRafRef.current = requestAnimationFrame(step);
      else tweenRafRef.current = 0;
    };
    tweenRafRef.current = requestAnimationFrame(step);
  };

  return (
    <div
      className="absolute inset-0 z-20"
      style={{ pointerEvents: "none" }}
      data-testid="player360-poi-arrow"
    >
      <div
        className="absolute"
        style={{
          left: x, top: y,
          transform: `translate(-50%, -50%) scale(${hovered ? 1.1 : 1})`,
          transition: "transform 150ms ease-out",
          pointerEvents: inGyro ? "none" : "auto",
        }}
      >
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onTouchStart={() => setHovered(true)}
          onTouchEnd={() => setHovered(false)}
          disabled={inGyro}
          className={
            "inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 backdrop-blur-md border border-white/30 shadow-[0_0_24px_rgba(255,255,255,0.35)] animate-pulse " +
            (inGyro ? "cursor-default" : "cursor-pointer hover:bg-white/25")
          }
          style={{ transform: `rotate(${rotDeg}deg)`, opacity: hovered ? 1 : 0.85 }}
          aria-label="Schau hier hin"
          data-testid="player360-poi-chevron"
        >
          <ChevronRight size={28} className="text-white drop-shadow" />
        </button>
        {poi.hint_text && (
          <div
            className="mt-2 px-2.5 py-1 rounded-md bg-black/70 backdrop-blur text-[11px] sm:text-xs text-white whitespace-nowrap text-center pointer-events-none"
            data-testid="player360-poi-hint"
          >
            {poi.hint_text}
          </div>
        )}
        {showTooltip && (
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-12 px-2.5 py-1.5 rounded-md bg-[#ff6b35] text-white text-[10px] sm:text-xs font-medium shadow-lg pointer-events-none animate-pulse whitespace-nowrap"
            data-testid="player360-poi-tooltip"
          >
            <span className="hidden sm:inline">💡 Klick auf den Pfeil oder dreh die Ansicht selbst</span>
            <span className="sm:hidden">💡 Antippen oder selbst drehen</span>
          </div>
        )}
      </div>
    </div>
  );
}
